import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as budgetsApi from "api/pianificazione/budgetsApi";
import { toast } from "react-toastify";
// Import filtri da userPreference
import { FiltriBudgets } from "components/userPreference/userPreferenceSlice";

// ============================================
// INTERFACES
// ============================================

export interface Budget {
    stagione: number;
    stagione_display: string;
    commerciale: number;
    commerciale_display: string;
    valore_budget: number;
    valore_confermato: number;
    valore_prodotto: number;
    percentuale_confermato: number;
    percentuale_prodotto: number;
}

export interface BudgetsList {
    budgets: Budget[];
    errorsStack: ErrorsStack;
    totali: {
        valore_budget_totale: number;
        valore_confermato_totale: number;
        valore_prodotto_totale: number;
        percentuale_confermato_totale: number;
        percentuale_prodotto_totale: number;
    };
}


export interface BudgetCommerciale {
    stagione: number;
    stagione_display: string;
    commerciale: number;
    commerciale_display: string;
    mandatario: number;
    mandatario_display: string;
    valore_budget: number;
    valore_confermato: number;
    valore_prodotto: number;
    percentuale_confermato: number;
    percentuale_prodotto: number;
}

export interface BudgetsCommercialiList {
    budgetsCommerciali: BudgetCommerciale[];
    errorsStack: ErrorsStack;
    totali: {
        valore_budget_totale: number;
        valore_confermato_totale: number;
        valore_prodotto_totale: number;
        percentuale_confermato_totale: number;
        percentuale_prodotto_totale: number;
    };
}

export interface BudgetMandatarioDettaglio {
    stagione: number;
    stagione_display: string;
    id: number;
    codice_item: string;
    descrizione_item: string;
    commerciale: number;
    mandatario: number;
    mandatario_display: string;
    fase_collezione: string;
    fase_collezione_display: string;
    tecnica?: string;
    tecnica_display?: string;
    px_prod?: number;
    impianto: string;
    px_camp?: number;
    qta_bdg?: number;

    valore_budget: number;
    valore_confermato: number;
    valore_prodotto: number;
    percentuale_confermato: number;
    percentuale_prodotto: number;
}

export interface BudgetsMandatariDettagliList {
    budgetsMandatariDettagli: BudgetMandatarioDettaglio[];
    errorsStack: ErrorsStack;
    totali: {
        valore_budget_totale: number;
        valore_confermato_totale: number;
        valore_prodotto_totale: number;
        percentuale_confermato_totale: number;
        percentuale_prodotto_totale: number;
    };
}

export interface BudgetTecnica {

    tecnica: number;
    tecnica_display: string;
    valore_budget: number;
    valore_confermato: number;
    valore_prodotto: number;
    capacita_esclusiva: number;
    capacita_condivisa: number;
}

export interface BudgetsTecnicheList {
    stagione: number;
    stagione_display: string;
    budgetsTecniche: BudgetTecnica[];
    errorsStack: ErrorsStack;
    totali: {
        valore_budget_totale: number;
        valore_confermato_totale: number;
        valore_prodotto_totale: number;
        percentuale_confermato_totale: number;
        percentuale_prodotto_totale: number;
        capacita_esclusiva_totale: number;
        capacita_condivisa_totale: number;
    };
}

export interface BudgetMandatario {
    mandatario: number | null;
    mandatario_display: string;
    valore_budget: number;
    valore_confermato: number;
    valore_prodotto: number;
}

export interface BudgetsMandatariList {
    stagione: number;
    stagione_display: string;
    budgetsMandatari: BudgetMandatario[];
    errorsStack: ErrorsStack;
    totali: {
        valore_budget_totale: number;
        valore_confermato_totale: number;
        valore_prodotto_totale: number;
        percentuale_confermato_totale: number;
        percentuale_prodotto_totale: number;
    };
}

// ============================================
// STATO SLICE
// ============================================

export interface BudgetsState {
    budgetsList: BudgetsList;
    budgetsCommercialiList: BudgetsCommercialiList;
    budgetsMandatariDettagliList: BudgetsMandatariDettagliList;
    budgetsTecnicheList: BudgetsTecnicheList;
    budgetsMandatariList: BudgetsMandatariList;
}

const initialState: BudgetsState = {
    budgetsList: {
        budgets: [],
        totali: {
            valore_budget_totale: 0,
            valore_confermato_totale: 0,
            valore_prodotto_totale: 0,
            percentuale_confermato_totale: 0,
            percentuale_prodotto_totale: 0
        },
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    budgetsCommercialiList: {
        budgetsCommerciali: [],
        totali: {
            valore_budget_totale: 0,
            valore_confermato_totale: 0,
            valore_prodotto_totale: 0,
            percentuale_confermato_totale: 0,
            percentuale_prodotto_totale: 0
        },
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    budgetsMandatariDettagliList: {
        budgetsMandatariDettagli: [],
        totali: {
            valore_budget_totale: 0,
            valore_confermato_totale: 0,
            valore_prodotto_totale: 0,
            percentuale_confermato_totale: 0,
            percentuale_prodotto_totale: 0
        },
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    budgetsTecnicheList: {
        stagione: 0,
        stagione_display: "",
        budgetsTecniche: [],
        totali: {
            valore_budget_totale: 0,
            valore_confermato_totale: 0,
            valore_prodotto_totale: 0,
            percentuale_confermato_totale: 0,
            percentuale_prodotto_totale: 0,
            capacita_esclusiva_totale: 0,
            capacita_condivisa_totale: 0,
        },
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    budgetsMandatariList: {
        stagione: 0,
        stagione_display: "",
        budgetsMandatari: [],
        totali: {
            valore_budget_totale: 0,
            valore_confermato_totale: 0,
            valore_prodotto_totale: 0,
            percentuale_confermato_totale: 0,
            percentuale_prodotto_totale: 0,
        },
        errorsStack: { status: ErrorStatusTypes.OK },
    },
};

export interface BudgetMandatario {
    mandatario: number | null;
    mandatario_display: string;
    valore_budget: number;
    valore_confermato: number;
    valore_prodotto: number;
}

export interface BudgetsMandatariList {
    stagione: number;
    stagione_display: string;
    budgetsMandatari: BudgetMandatario[];
    errorsStack: ErrorsStack;
    totali: {
        valore_budget_totale: number;
        valore_confermato_totale: number;
        valore_prodotto_totale: number;
        percentuale_confermato_totale: number;
        percentuale_prodotto_totale: number;
    };
}

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchBudgets = createAsyncThunk(
    "budgets/fetchBudgets",
    async (parametri: {stagione_id:number} ) => {
        return await budgetsApi.fetchBudgets(parametri.stagione_id);
    }
);

export const fetchBudgetsCommerciali = createAsyncThunk(
    "budgets/fetchBudgetsCommerciali",
    async (parametri:  {stagione_id:number, commerciale_id:number}) => {
        return await budgetsApi.fetchBudgetsCommerciali(parametri.stagione_id, parametri.commerciale_id);
    }
);
export const fetchBudgetsCommercialiMandatari = createAsyncThunk(
    "budgets/fetchBudgetsCommercialiMandatari",
    async (parametri: { stagione_id: number, mandatario_id: number, commerciale_id?: number }) => {
        return await budgetsApi.fetchBudgetsCommercialiMandatari(
            parametri.stagione_id,
            parametri.mandatario_id,
            parametri.commerciale_id,
        );
    }
);
export const fetchBudgetsTecniche = createAsyncThunk(
    "budgets/fetchBudgetsTecniche",
    async (parametri:  {stagione_id:number,tecniche?: number[]}) => {
        return await budgetsApi.fetchBudgetsTecniche(parametri.stagione_id, parametri.tecniche);
    }
);

export const fetchBudgetsMandatari = createAsyncThunk(
    "budgets/fetchBudgetsMandatari",
    async (parametri: { stagione_id: number }) => {
        return await budgetsApi.fetchBudgetsMandatari(parametri.stagione_id);
    }
);



// ============================================
// SLICE
// ============================================

export const budgetsSlice = createSlice({
    name: "budgets",
    initialState,
    reducers: {
        resetBudgets: (state) => {
            state.budgetsList = initialState.budgetsList;
        },
    },
    extraReducers: (builder) => {
        // ============================================
        // FETCH (Lista)
        // ============================================
        builder.addCase(fetchBudgets.pending, (state) => {
            state.budgetsList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchBudgets.fulfilled, (state, action) => {
            state.budgetsList = action.payload;
            state.budgetsList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchBudgets.rejected, (state, action) => {
            state.budgetsList.errorsStack = parseErrorMessage(action.error);
        });

        builder.addCase(fetchBudgetsCommerciali.pending, (state) => {
            state.budgetsCommercialiList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchBudgetsCommerciali.fulfilled, (state, action) => {
            state.budgetsCommercialiList.budgetsCommerciali = [
                ...state.budgetsCommercialiList.budgetsCommerciali.filter(bc => bc.commerciale !== action.payload.budgetsCommerciali[0]?.commerciale),
                ...action.payload.budgetsCommerciali,
            ]
            state.budgetsCommercialiList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchBudgetsCommerciali.rejected, (state, action) => {
            state.budgetsCommercialiList.errorsStack = parseErrorMessage(action.error);
        });

        builder.addCase(fetchBudgetsCommercialiMandatari.pending, (state) => {
            state.budgetsMandatariDettagliList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchBudgetsCommercialiMandatari.fulfilled, (state, action) => {
            // state.budgetsMandatariDettagliList = action.payload;

            state.budgetsMandatariDettagliList.budgetsMandatariDettagli = [
                ...state.budgetsMandatariDettagliList.budgetsMandatariDettagli.filter(bc => bc.mandatario !== action.payload.budgetsMandatariDettagli[0]?.mandatario),
                ...action.payload.budgetsMandatariDettagli,
            ]


            state.budgetsMandatariDettagliList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchBudgetsCommercialiMandatari.rejected, (state, action) => {
            state.budgetsMandatariDettagliList.errorsStack = parseErrorMessage(action.error);
        });

        builder.addCase(fetchBudgetsTecniche.pending, (state) => {
            state.budgetsTecnicheList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchBudgetsTecniche.fulfilled, (state, action) => {
            state.budgetsTecnicheList = action.payload
            state.budgetsTecnicheList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchBudgetsTecniche.rejected, (state, action) => {
            state.budgetsTecnicheList.errorsStack = parseErrorMessage(action.error);
        });

        builder.addCase(fetchBudgetsMandatari.pending, (state) => {
            state.budgetsMandatariList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchBudgetsMandatari.fulfilled, (state, action) => {
            state.budgetsMandatariList = action.payload;
            state.budgetsMandatariList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchBudgetsMandatari.rejected, (state, action) => {
            state.budgetsMandatariList.errorsStack = parseErrorMessage(action.error);
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    resetBudgets
} = budgetsSlice.actions;

export const budgetsSliceReducer = budgetsSlice.reducer;